import { useEffect, useState } from 'react';
import { SLOTS, STATUSES, STATUS_LABEL, STATUS_NEEDS_REASON, formatDateTime } from '@nu/shared';
import Modal from '../../components/Modal.jsx';
import { api } from '../../api/client.js';
import { problem } from './util.js';
import AddressFinder from './AddressFinder.jsx';
import JobPhotos from '../../components/JobPhotos.jsx';
import JobStock from './JobStock.jsx';
import { BookingDefects } from './Defects.jsx';
import { BookingIncidents, BookingSupport } from '../incidents/Incidents.jsx';

// One booking: details (editable by the office), status, notes for the
// engineers, and the history of changes. id null = a new booking.
const EMPTY = {
  typeId: '', clientRef: '', customerName: '', customerPhone: '',
  addressLine1: '', addressLine2: '', town: '', postcode: '', propertyId: '', accessNotes: '',
  bookingDate: '', slot: '', teamId: '',
};

function toForm(b) {
  const f = {};
  for (const k of Object.keys(EMPTY)) f[k] = b[k] === null || b[k] === undefined ? '' : String(b[k]);
  return f;
}

function toBody(f) {
  const body = {};
  for (const k of Object.keys(EMPTY)) body[k] = f[k] === '' ? null : f[k];
  body.typeId = f.typeId ? Number(f.typeId) : null;
  body.teamId = f.teamId ? Number(f.teamId) : null;
  body.propertyId = f.propertyId ? Number(f.propertyId) : null;
  return body;
}

export default function BookingModal({ id, prefill, types, teams, canEdit, onClose, onSaved }) {
  const isNew = !id;
  const [booking, setBooking] = useState(null);
  const [f, setF] = useState(null);
  const [note, setNote] = useState('');
  const [status, setStatus] = useState('');
  const [reason, setReason] = useState('');
  const [error, setError] = useState(null);
  const [info, setInfo] = useState(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    setError(null);
    if (isNew) {
      setF({
        ...EMPTY,
        typeId: String(types.find((t) => t.active)?.id || ''),
        teamId: prefill?.teamId ? String(prefill.teamId) : '',
        bookingDate: prefill?.bookingDate || '',
      });
      return;
    }
    api(`/bookings/${id}`).then((b) => {
      setBooking(b);
      setF(toForm(b));
      setStatus(b.status);
      setReason(b.statusReason || '');
    }, (e) => setError(problem(e)));
  }, [id, isNew, types, prefill]);

  // A message at the top of the pop-up: bring it into view (you may have
  // scrolled down to the buttons).
  useEffect(() => {
    if (error || info) document.querySelector('.booking-msg')?.scrollIntoView({ block: 'nearest', behavior: 'smooth' });
  }, [error, info]);

  const reload = async () => {
    const b = await api(`/bookings/${id}`);
    setBooking(b);
    setF(toForm(b));
    setStatus(b.status);
    setReason(b.statusReason || '');
  };

  const run = async (fn) => {
    setBusy(true);
    setError(null);
    setInfo(null);
    try { await fn(); } catch (e) { setError(problem(e)); } finally { setBusy(false); }
  };

  const save = (e) => {
    e?.preventDefault();
    return run(async () => {
      if (isNew) {
        const out = await api('/bookings', { method: 'POST', body: { ...toBody(f), note: note.trim() || undefined } });
        onSaved(`${out.reference} booked.`, out.id);
        return;
      }
      const body = toBody(f);
      if (status !== booking.status || (STATUS_NEEDS_REASON.includes(status) && reason !== (booking.statusReason || ''))) {
        body.status = status;
        body.statusReason = reason;
      }
      const out = await api(`/bookings/${id}`, { method: 'PUT', body });
      // Saved: close, so the page's "saved" line shows. Nothing changed: say so here.
      if (!out.changed) { await reload(); setInfo('Nothing to save: no changes made.'); return; }
      onSaved(`${booking.reference} saved.`);
      onClose();
    });
  };

  const addNote = () => run(async () => {
    await api(`/bookings/${id}/notes`, { method: 'POST', body: { body: note } });
    setNote('');
    await reload();
    onSaved(`Note added to ${booking.reference}.`);
  });

  const set = (k) => (e) => setF((s) => ({ ...s, [k]: e.target.value }));
  const title = isNew ? 'New booking' : booking ? `${booking.reference}` : 'Booking';
  const ro = !canEdit;
  const activeTeams = teams.filter((t) => t.active || String(t.id) === f?.teamId);

  return (
    <Modal
      open
      wide
      title={title}
      onClose={onClose}
      footer={(
        <>
          <button type="button" className="btn" onClick={onClose}>{ro ? 'Close' : 'Cancel'}</button>
          {!ro && f && <button type="submit" form="booking-form" className="btn primary" disabled={busy}>{isNew ? 'Book' : 'Save changes'}</button>}
        </>
      )}
    >
      {error && <p className="notice-line notice-bad booking-msg" role="alert">{error}</p>}
      {info && <p className="notice-line notice-warn booking-msg" role="status">{info}</p>}
      {!f && !error && <p className="muted">Loading…</p>}
      {f && (
        <form id="booking-form" className="form" onSubmit={save}>
          {booking && (
            <p className="small muted">
              <span className={`status status-${booking.status}`}>{STATUS_LABEL[booking.status]}</span>
              {booking.statusReason && ` ${booking.statusReason}.`} Booked by {booking.createdByEmail} on {formatDateTime(booking.createdAt)}.
            </p>
          )}
          {booking?.revisitOf && (
            <p className="notice-line notice-warn">Revisit for a defect on {booking.revisitOf.reference}. It uses half a slot on its day.{!booking.bookingDate ? ' Give it a date once Network Plus has confirmed one with the customer.' : ''}</p>
          )}
          {booking?.jobNotes && (
            <div className="job-notes-office">
              <strong>Engineer&apos;s notes</strong>
              <p>{booking.jobNotes}</p>
            </div>
          )}

          <fieldset className="fld-group" disabled={ro}>
            <legend>When and who</legend>
            <div className="fld-row">
              <label className="fld narrow">Date<input type="date" value={f.bookingDate} onChange={set('bookingDate')} /></label>
              <label className="fld narrow">
                Slot
                <select value={f.slot} onChange={set('slot')}>
                  <option value="">Not set</option>
                  {SLOTS.map((s) => <option key={s.value} value={s.value}>{s.label}</option>)}
                </select>
              </label>
              <label className="fld">
                Team
                <select value={f.teamId} onChange={set('teamId')}>
                  <option value="">Not on a team yet</option>
                  {activeTeams.map((t) => <option key={t.id} value={t.id}>{t.name}</option>)}
                </select>
              </label>
            </div>
            {f.teamId && !f.bookingDate && <p className="small warn-text">Give it a date to put it on a team.</p>}
          </fieldset>

          <fieldset className="fld-group" disabled={ro}>
            <legend>Job</legend>
            <div className="fld-row">
              <label className="fld">
                Type
                <select value={f.typeId} onChange={set('typeId')} required>
                  {types.filter((t) => t.active || String(t.id) === f.typeId).map((t) => <option key={t.id} value={t.id}>{t.client} · {t.workType}</option>)}
                </select>
              </label>
              <label className="fld">Client reference<input value={f.clientRef} onChange={set('clientRef')} maxLength={60} placeholder="Network Plus job ref, if any" /></label>
            </div>
            {booking?.np && (
              <p className="small muted np-line">
                From the Network Plus schedule: {[booking.np.status && `status ${booking.np.status}`, booking.np.permit && `permit ${booking.np.permit}`, booking.np.area && `area ${booking.np.area}`, booking.np.gang && `gang ${booking.np.gang}`].filter(Boolean).join(' · ')}
              </p>
            )}
          </fieldset>

          <fieldset className="fld-group" disabled={ro}>
            <legend>Address and customer</legend>
            {!ro && (
              <AddressFinder
                autoFocus={isNew}
                linked={Boolean(f.propertyId)}
                onPick={(p) => setF((x) => ({
                  ...x,
                  addressLine1: [p.organisation, p.line1].filter(Boolean).join(', '),
                  addressLine2: [p.line2, p.line3].filter(Boolean).join(', '),
                  town: p.postTown,
                  postcode: p.postcode,
                  propertyId: p.id ? String(p.id) : '',
                }))}
              />
            )}
            {booking?.uprn && <p className="small muted">Property reference (UPRN): {booking.uprn}</p>}
            <label className="fld">Address<input value={f.addressLine1} onChange={set('addressLine1')} maxLength={200} required /></label>
            <label className="fld">Address line 2<input value={f.addressLine2} onChange={set('addressLine2')} maxLength={200} /></label>
            <div className="fld-row">
              <label className="fld">Town<input value={f.town} onChange={set('town')} maxLength={100} /></label>
              <label className="fld narrow">Postcode<input value={f.postcode} onChange={set('postcode')} maxLength={8} required autoCapitalize="characters" /></label>
            </div>
            <div className="fld-row">
              <label className="fld">Customer name<input value={f.customerName} onChange={set('customerName')} maxLength={200} /></label>
              <label className="fld">Customer phone<input type="tel" value={f.customerPhone} onChange={set('customerPhone')} maxLength={25} /></label>
            </div>
            <label className="fld">Access notes<textarea value={f.accessNotes} onChange={set('accessNotes')} maxLength={2000} placeholder="Where the meter is, parking, gate codes, pets…" /></label>
          </fieldset>

          {!isNew && booking && (
            <fieldset className="fld-group" disabled={ro}>
              <legend>Status</legend>
              <p className="small muted">Booked and Assigned change on their own when a team and date are set.</p>
              <div className="fld-row">
                <label className="fld">
                  Status
                  <select value={status} onChange={(e) => setStatus(e.target.value)}>
                    {STATUSES.map((s) => <option key={s.value} value={s.value}>{s.label}</option>)}
                  </select>
                </label>
                {STATUS_NEEDS_REASON.includes(status) && (
                  <label className="fld">Reason<input value={reason} onChange={(e) => setReason(e.target.value)} maxLength={255} required /></label>
                )}
              </div>
            </fieldset>
          )}
          {!isNew && booking?.timing && (booking.timing.onRouteAt || booking.timing.arrivedAt) && <JobTiming t={booking.timing} />}

          {isNew && !ro && (
            <label className="fld">Note for the engineers<textarea value={note} onChange={(e) => setNote(e.target.value)} maxLength={2000} placeholder="Optional. They see it in the app." /></label>
          )}
        </form>
      )}

      {booking && (
        <section className="booking-notes">
          <BookingDefects booking={booking} teams={teams} canEdit={!ro} onChanged={(text) => { reload(); onSaved(text); }} />
          <BookingIncidents bookingId={booking.id} />
          <BookingSupport bookingId={booking.id} />
          <JobStock booking={booking} canEdit={!ro} onSaved={reload} />
          <JobPhotos base={`/bookings/${booking.id}/photos`} title="Photos from the engineers" digitiseId={booking.id} digitiseRef={booking.reference} />
          <h3>Notes for the engineers</h3>
          <p className="small muted">The team on this booking sees these in the app.</p>
          {!ro && (
            <div className="note-add">
              <textarea value={note} onChange={(e) => setNote(e.target.value)} maxLength={2000} placeholder="Add a note" aria-label="New note" />
              <button type="button" className="btn" disabled={busy || !note.trim()} onClick={addNote}>Add note</button>
            </div>
          )}
          {!booking.notes.length && <p className="muted small">No notes yet.</p>}
          <ul className="history">
            {booking.notes.map((n) => (
              <li key={n.id}>
                <span className="note-body">{n.body}</span>
                <span className="muted small">{n.authorName} · {formatDateTime(n.createdAt)}</span>
              </li>
            ))}
          </ul>

          <details className="booking-history">
            <summary>History ({booking.history.length})</summary>
            <ul className="history">
              {booking.history.map((h) => (
                <li key={h.id}>
                  <span>{h.summary}</span>
                  <span className="muted small">{formatDateTime(h.changedAt)} · {h.actorEmail}</span>
                </li>
              ))}
            </ul>
          </details>
        </section>
      )}
    </Modal>
  );
}

const hm = (m) => (m == null ? '–' : m < 60 ? `${m}m` : `${Math.floor(m / 60)}h ${String(m % 60).padStart(2, '0')}m`);
const clock = (iso) => (iso ? formatDateTime(iso).slice(-5) : '–');

// How long the job took: when the team set off, arrived and finished, and
// the time travelling, on site (less any pause) and in all. A job still
// going shows the time so far.
function JobTiming({ t }) {
  return (
    <fieldset className="fld-group job-timing">
      <legend>Time on job{t.finished ? '' : ' (so far)'}</legend>
      <div className="job-timing-row">
        <div><span className="small muted">On route</span><strong>{clock(t.onRouteAt)}</strong></div>
        <div><span className="small muted">Arrived</span><strong>{clock(t.arrivedAt)}</strong></div>
        <div><span className="small muted">Work started</span><strong>{clock(t.workStartedAt)}</strong><span className="small muted">risk assessment page 2</span></div>
        <div><span className="small muted">Finished</span><strong>{t.finished ? clock(t.finishedAt) : '–'}</strong></div>
      </div>
      <div className="job-timing-row">
        <div><span className="small muted">Travel</span><strong>{hm(t.travelMins)}</strong></div>
        <div><span className="small muted">Setting up</span><strong>{hm(t.setupMins)}</strong></div>
        <div><span className="small muted">Working</span><strong>{hm(t.workMins)}</strong>{t.pausedMins > 0 && <span className="small muted">+ {hm(t.pausedMins)} paused</span>}</div>
        <div><span className="small muted">Total</span><strong>{hm(t.totalMins)}</strong></div>
      </div>
    </fieldset>
  );
}
